import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { isValidEmail } from "@/lib/validation";

const empty = { last_name: "", first_name: "", phone: "", email: "", seat_category: "orchestre", sponsor_name: "", nb_inv_supp: "0" };

export const HavInviteTab = () => {
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const set = (k: keyof typeof empty) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.last_name.trim() || !form.first_name.trim() || !isValidEmail(form.email.trim())) {
      toast({ title: "Champs invalides", description: "Nom, prénom et email valide sont obligatoires.", variant: "destructive" });
      return;
    }
    const extra = Number(form.nb_inv_supp || 0);
    if (!Number.isInteger(extra) || extra < 0 || extra > 50) {
      toast({ title: "Champs invalides", description: "Le nombre d'invités supplémentaires doit être entre 0 et 50.", variant: "destructive" });
      return;
    }
    setSaving(true);
    const { data, error } = await supabase.rpc("insert_hav_invite" as never, {
      p_first_name: form.first_name,
      p_last_name: form.last_name,
      p_email: form.email,
      p_phone: form.phone,
      p_sponsor_name: form.sponsor_name,
      p_seat_category: form.seat_category,
      p_extra_guests: extra,
    } as never);
    setSaving(false);
    if (error) {
      toast({ title: "Erreur", description: error.message, variant: "destructive" });
      return;
    }
    const code = (data as unknown as { ticket_code: string }[] | null)?.[0]?.ticket_code;
    toast({ title: "Invité enregistré", description: `${form.first_name} ${form.last_name}${code ? ` — ${code}` : ""}${extra > 0 ? ` + ${extra} invité(s) supplémentaire(s)` : ""}` });
    setForm(empty);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Invitation « À Haute VOIX » — inscrire un invité (gratuit)</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={submit} className="grid gap-4 sm:grid-cols-2">
          <div><Label>Nom *</Label><Input value={form.last_name} onChange={set("last_name")} maxLength={100} /></div>
          <div><Label>Prénom *</Label><Input value={form.first_name} onChange={set("first_name")} maxLength={100} /></div>
          <div><Label>Téléphone</Label><Input value={form.phone} onChange={set("phone")} maxLength={30} /></div>
          <div><Label>Email *</Label><Input type="email" value={form.email} onChange={set("email")} maxLength={255} /></div>
          <div>
            <Label>Place Catégorie *</Label>
            <select value={form.seat_category} onChange={set("seat_category")} className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm">
              <option value="orchestre">Orchestre</option>
              <option value="balcon">Balcon</option>
            </select>
          </div>
          <div><Label>Parrain</Label><Input value={form.sponsor_name} onChange={set("sponsor_name")} maxLength={200} /></div>
          <div>
            <Label>Nombre d'invités supplémentaires</Label>
            <Input type="number" min={0} max={50} step={1} value={form.nb_inv_supp} onChange={set("nb_inv_supp")} />
            <p className="mt-1 text-xs text-muted-foreground">Chacun reçoit son propre identifiant, avec l'email de l'invité principal.</p>
          </div>
          <div className="sm:col-span-2">
            <Button type="submit" disabled={saving} className="bg-[#00123A] hover:bg-[#00123A]/90">
              {saving ? "Enregistrement..." : "Inscrire l'invité"}
            </Button>
            <p className="mt-2 text-xs text-muted-foreground">
              L'invité apparaît ensuite dans l'onglet « Soirée AHV » avec le type « Invité ». Validez-le là pour lui envoyer l'email de confirmation (sans CERFA).
            </p>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};
